import React, { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Store,
  Truck,
  IndianRupee,
  Newspaper,
  PackagePlus,
  Package,
  Users,
  FileBarChart2,
  Sparkles,
  Wallet,
  AlertCircle,
  Snowflake,
  ArrowLeftRight,
  Activity,
  Beef,
  PartyPopper,
  Warehouse,
  Settings as SettingsIcon,
  LogOut,
  Menu,
  X,
} from "lucide-react";
import { useAuth } from "../lib/auth";
import InstallAppBanner from "./InstallAppBanner";

const NAV_ALL = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard", role: "owner" },
  { to: "/pos", label: "Retail POS", icon: Store, testid: "nav-pos", role: "any" },
  { to: "/wholesale", label: "Wholesale", icon: Truck, testid: "nav-wholesale", role: "any" },
  { to: "/party-orders", label: "Party Orders", icon: PartyPopper, testid: "nav-party-orders", role: "any" },
  { to: "/rates", label: "Daily Rates", icon: IndianRupee, testid: "nav-rates", role: "owner" },
  { to: "/paper-rates", label: "Paper Rate", icon: Newspaper, testid: "nav-paper-rates", role: "owner" },
  { to: "/purchases", label: "Purchases", icon: PackagePlus, testid: "nav-purchases", role: "owner" },
  { to: "/freezer-stock", label: "Freezer Stock", icon: Snowflake, testid: "nav-freezer", role: "owner" },
  { to: "/freezer-inout", label: "Freezer In/Out", icon: ArrowLeftRight, testid: "nav-freezer-inout", role: "owner" },
  { to: "/freezer-analytics", label: "Freezer Analytics", icon: Activity, testid: "nav-freezer-analytics", role: "owner" },
  { to: "/expenses", label: "Expenses", icon: Wallet, testid: "nav-expenses", role: "owner" },
  { to: "/products", label: "Products", icon: Package, testid: "nav-products", role: "owner" },
  { to: "/customers", label: "Customers", icon: Users, testid: "nav-customers", role: "owner" },
  { to: "/suppliers", label: "Suppliers", icon: Warehouse, testid: "nav-suppliers", role: "owner" },
  { to: "/receivables", label: "Receivables", icon: AlertCircle, testid: "nav-receivables", role: "owner" },
  { to: "/payables", label: "Payables", icon: Truck, testid: "nav-payables", role: "owner" },
  { to: "/reports", label: "Reports", icon: FileBarChart2, testid: "nav-reports", role: "owner" },
  { to: "/insights", label: "Insights", icon: Sparkles, testid: "nav-insights", role: "owner" },
  { to: "/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings", role: "owner" },
];

const NavList = ({ items, onClick }) => (
  <nav className="flex-1 px-3 py-4 space-y-0.5">
    {items.map(({ to, label, icon: Icon, testid }) => (
      <NavLink
        key={to}
        to={to}
        end={to === "/"}
        data-testid={testid}
        onClick={onClick}
        className={({ isActive }) =>
          `flex items-center gap-3 px-3 py-3 text-sm font-medium transition-colors duration-200 ${
            isActive
              ? "bg-zinc-950 text-white"
              : "text-zinc-700 hover:bg-zinc-100"
          }`
        }
      >
        <Icon className="h-4 w-4" strokeWidth={2} />
        <span>{label}</span>
      </NavLink>
    ))}
  </nav>
);

const BrandBlock = () => (
  <div className="px-6 py-5 border-b border-zinc-200">
    <div className="flex items-center gap-3">
      <img
        src="/eeman-logo.jpg"
        alt="EEMAN MEATS"
        className="h-10 w-10 object-contain bg-white"
        data-testid="sidebar-logo"
      />
      <div>
        <div className="font-display font-bold text-lg tracking-tight">EEMAN MEATS</div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">
          Accounting · POS
        </div>
      </div>
    </div>
  </div>
);

const Footer = ({ role, logout }) => (
  <div className="px-3 py-3 border-t border-zinc-200">
    <div className="px-3 mb-2 text-[10px] uppercase tracking-[0.2em] text-zinc-500">
      Signed in as
    </div>
    <div className="px-3 mb-3 flex items-center gap-2">
      <span
        className={`px-2 py-0.5 text-[10px] uppercase tracking-wider ${
          role === "owner" ? "bg-sky-600 text-white" : "bg-zinc-900 text-white"
        }`}
        data-testid="role-badge"
      >
        {role}
      </span>
    </div>
    <button
      onClick={logout}
      data-testid="logout-btn"
      className="w-full flex items-center gap-3 px-3 py-2 text-xs uppercase tracking-wider text-zinc-600 hover:bg-zinc-100"
    >
      <LogOut className="h-4 w-4" />
      Logout
    </button>
  </div>
);

export default function Layout({ children }) {
  const location = useLocation();
  const { role, logout } = useAuth();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const isPOSFullscreen = location.pathname === "/pos" || location.pathname === "/wholesale";

  const NAV = NAV_ALL.filter((n) => n.role === "any" || n.role === role);
  const current = NAV.find((n) => n.to === location.pathname) || NAV[0];

  return (
    <div className="flex min-h-screen bg-zinc-50">
      {/* Desktop sidebar */}
      <aside
        className="hidden lg:flex w-60 flex-col border-r border-zinc-200 bg-white sticky top-0 h-screen"
        data-testid="sidebar"
      >
        <BrandBlock />
        <NavList items={NAV} />
        <Footer role={role} logout={logout} />
      </aside>

      {/* Mobile header */}
      <header
        className="lg:hidden fixed top-0 inset-x-0 z-30 bg-white border-b border-zinc-200 flex items-center justify-between px-4 py-3"
        data-testid="mobile-header"
      >
        <button
          onClick={() => setDrawerOpen(true)}
          data-testid="mobile-menu-btn"
          className="p-1.5 -ml-1 text-zinc-700 hover:text-sky-600"
        >
          <Menu className="h-6 w-6" />
        </button>
        <div className="flex items-center gap-2">
          <img
            src="/eeman-logo.jpg"
            alt="EEMAN"
            className="h-7 w-7 object-contain"
          />
          <span className="font-display font-bold text-base tracking-tight">
            {current?.label || "EEMAN MEATS"}
          </span>
        </div>
        <button
          onClick={logout}
          data-testid="mobile-logout"
          className="p-1.5 -mr-1 text-zinc-500 hover:text-rose-600"
        >
          <LogOut className="h-5 w-5" />
        </button>
      </header>

      {/* Mobile drawer */}
      {drawerOpen && (
        <>
          <div
            className="lg:hidden fixed inset-0 z-40 bg-black/50 backdrop-blur-sm"
            onClick={() => setDrawerOpen(false)}
            data-testid="drawer-backdrop"
          />
          <aside
            className="lg:hidden fixed top-0 left-0 z-50 w-72 max-w-[85vw] h-full bg-white flex flex-col shadow-2xl"
            data-testid="mobile-drawer"
          >
            <div className="flex items-center justify-between border-b border-zinc-200">
              <BrandBlock />
              <button
                onClick={() => setDrawerOpen(false)}
                className="p-3 text-zinc-500 hover:text-zinc-900"
                data-testid="drawer-close-btn"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              <NavList items={NAV} onClick={() => setDrawerOpen(false)} />
            </div>
            <Footer role={role} logout={logout} />
          </aside>
        </>
      )}

      <main
        className={`flex-1 ${isPOSFullscreen ? "" : "pb-4"} pt-14 lg:pt-0`}
      >
        {children}
      </main>
      <InstallAppBanner />
    </div>
  );
}
